import { z } from 'zod';
import { BRAND_STATUSES } from '../brand-status.ts';
import { BrandKindQuery, IdParams, IsoDateTime, Ok } from '../common.ts';
import { ONBOARDING_DAYS_MAX, ONBOARDING_DAYS_MIN, SITE_KINDS } from '../probe-values.ts';
import { defineRoute } from '../route.ts';

// ── 고객 ────────────────────────────────────────────────────────────────
/** 목표 브랜드명 — 첫 번째가 대표 표기. 고객 단위다 (261008-client-is-brand) */
export const BrandNames = z
  .array(z.string().trim().min(1).max(120))
  .min(1, '목표 브랜드명이 하나 이상 필요하다')
  .max(20);

/** 브랜드 프로필(마크다운) 상한 — 서버는 해석하지 않는다 */
export const PROFILE_MAX_CHARS = 50_000;

export const Brand = z.object({
  id: z.uuid(),
  name: z.string(),
  queryCount: z.number().int(),
  /** 자가 가입은 pending 으로 들어온다. 운영자가 만든 고객은 처음부터 active. 확정 뒤 결제 전은 confirmed */
  status: z.enum(BRAND_STATUSES).default('active'),
  approvedAt: IsoDateTime.nullable().default(null),
  /**
   * ★ 고객의 원본 자산. 블로그 사이트 제작의 입력이다 — intake 가 홈페이지를 긁고,
   * 플레이스는 주소·전화·진료시간의 검증 출처이자 JSON-LD `sameAs` 가 된다.
   * 고객 자신의 정보이므로 `scoped` 응답에 실어도 된다(우리 사이트·워커·진단과 다르다).
   */
  homepageUrl: z.string().nullable().default(null),
  naverPlaceUrl: z.string().nullable().default(null),
  /** 그 밖의 원본 자산 링크(블로그·SNS 등) — 고객 자신의 정보라 scoped 응답에 실어도 된다 */
  links: z.array(z.string()).default([]),
  /**
   * 목표 브랜드명 (261008-client-is-brand). 비어 있으면 질문을 만들 수 없다 — 가입 직후 고객이 그 상태다.
   * 고객 자신의 이름이고 리포트에 이미 나오므로 `scoped` 응답에 실어도 된다
   */
  brandNames: z.array(z.string()).default([]),
  /** 살아 있는 소속 계정 수 — 0 이면 「주인 없는 브랜드」다 */
  accountCount: z.number().int().default(0),
  /**
   * 소유 계정들의 회사명(가입의 업체명 — 261013-signup-company-name-to-account). **운영자 응답에만** 채운다 —
   * 다른 계정의 정보라 같은 브랜드의 관리자에게도 보이지 않는다(비-admin 은 `[]`)
   */
  ownerCompanyNames: z.array(z.string()).default([]),
  /**
   * 판매 대행사 이름(261025-agency-white-label). **운영자 응답에만** 채운다 — 대행사 정보는 `brandingViewOf` 밖으로
   * 비-admin 응답에 싣지 않는다(비-admin 은 null). null 은 liveGPT 직접 고객이다
   */
  agencyName: z.string().nullable().default(null),
  /** 테스트 브랜드 — 운영자 화면의 「테스트」 탭에만 나온다(261023-test-brand) */
  isTest: z.boolean().default(false),
  createdAt: IsoDateTime,
});
export type Brand = z.infer<typeof Brand>;

/**
 * 고객 한 곳 — 목록에 없는 **브랜드 프로필**이 붙는다. 목록에 실으면 고객 수 × 최대 50KB 가 된다
 */
export const BrandDetail = Brand.extend({
  /** 마크다운. 고객이 준 사실의 원천(SSOT) — `targets-export` 가 `content/brand-profile.md` 로 내보낸다 */
  profile: z.string().default(''),
  /** 프로필 **본문**이 바뀐 시각. 브랜드명만 고친 것으로는 움직이지 않는다 */
  profileUpdatedAt: IsoDateTime.nullable().default(null),
});
export type BrandDetail = z.infer<typeof BrandDetail>;

const Profile = z.string().max(PROFILE_MAX_CHARS, `브랜드 프로필은 ${PROFILE_MAX_CHARS.toLocaleString()}자까지입니다`);

/**
 * 원본 자산 URL. **형식을 좁게 잡지 않는다** — 네이버 플레이스는 `map.naver.com/p/entry/place/<id>`,
 * `m.place.naver.com`, `naver.me` 단축 링크가 전부 돌아다니고, 좁히면 운영자가 정상 링크를 못 넣는다.
 * 숫자 id 추출은 **쓰는 쪽**(intake)이 한다 — 단축 링크는 따라가야 알 수 있다.
 */
export const AssetUrl = z
  .string()
  .trim()
  .url('http(s) URL 이어야 합니다')
  .max(500)
  .refine((u) => /^https?:\/\//i.test(u), 'http(s) URL 이어야 합니다');

/** 추가 링크 상한 — 블로그·SNS·카카오 채널 등 (261010-agency-console) */
export const LINKS_MAX = 20;
export const ExtraLinks = z.array(AssetUrl).max(LINKS_MAX, `링크는 ${LINKS_MAX}개까지입니다`);

export const HOMEPAGE_REQUIRED_MESSAGE =
  '대표 웹페이지 주소를 넣어 주세요 — 홈페이지·네이버 블로그 등 가장 주로 쓰는 곳이면 됩니다';

/**
 * 고객을 **만드는** 경로(자가 가입·대행사 고객·브랜드 추가)의 링크 칸 (운영자 결정 2026-10-11):
 *   홈페이지      **필수** — 좁은 뜻의 홈페이지가 아니라 **고객이 가장 주로 쓰는 웹페이지**다(네이버 블로그 등도 된다).
 *                 화면에서는 「대표 웹페이지」라고 부른다. 원본 자산이 없으면 콘텐츠를 만들 재료가 없다
 *   네이버 플레이스  선택·권장 — 주소·전화·진료시간의 검증 출처이자 구조화 데이터의 sameAs
 *   기타 링크      선택(GEO 콘텐츠 작성 참고용) — 블로그·SNS·카카오 채널 등
 * 운영자 경로(`createBrand`)는 요구하지 않는다(준비 중인 고객을 먼저 만들 때가 있다)
 */
export const AssetLinks = {
  homepageUrl: AssetUrl,
  naverPlaceUrl: AssetUrl.nullable().default(null),
  links: ExtraLinks.default([]),
};

export const listBrands = defineRoute({
  method: 'get',
  path: '/api/brands',
  access: 'scoped',
  summary: '내 범위의 고객 목록',
  /** 운영자에게만 적용된다 — 고객 범위 호출은 무시하고 자기 브랜드 전부 */
  query: BrandKindQuery,
  response: z.object({ brands: z.array(Brand) }),
});
export const getBrand = defineRoute({
  method: 'get',
  path: '/api/brands/{id}',
  access: 'scoped',
  summary: '고객 한 곳 (브랜드 프로필 포함)',
  params: IdParams,
  response: z.object({ brand: BrandDetail }),
});
export const createBrand = defineRoute({
  method: 'post',
  path: '/api/brands',
  access: 'admin',
  summary: '고객 생성',
  body: z
    .object({
      name: z.string().trim().min(1).max(120),
      homepageUrl: AssetUrl.nullable().optional(),
      naverPlaceUrl: AssetUrl.nullable().optional(),
      links: ExtraLinks.optional(),
      brandNames: BrandNames.optional(),
      profile: Profile.optional(),
      /** 테스트 브랜드로 만든다 — 생략하면 실제 브랜드 */
      isTest: z.boolean().optional(),
    })
    .strict(),
  response: z.object({ brand: BrandDetail, warnings: z.array(z.string()) }),
});
export const updateBrand = defineRoute({
  method: 'patch',
  path: '/api/brands/{id}',
  access: 'admin',
  summary: '브랜드 변경 — 브랜드명을 바꾸면 그 브랜드 질문의 과거 샘플이 다시 판정된다',
  params: IdParams,
  body: z
    .object({
      name: z.string().trim().min(1).max(120).optional(),
      // null 은 "지운다", 빠지면 "그대로 둔다" — 둘을 가른다
      homepageUrl: AssetUrl.nullable().optional(),
      naverPlaceUrl: AssetUrl.nullable().optional(),
      links: ExtraLinks.optional(),
      /** 비울 수는 없다 — 질문 판정이 이 값을 복사해 쓴다 */
      brandNames: BrandNames.optional(),
      profile: Profile.optional(),
    })
    .strict(),
  response: z.object({ brand: BrandDetail, warnings: z.array(z.string()) }),
});
/** 브랜드의 소속 계정 — 운영자 화면 전용. 역할 바꾸기·빼기는 `/api/memberships/{id}`, 더하기는 계정 화면에서 */
export const BrandMemberAdmin = z.object({
  membershipId: z.uuid(),
  userId: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  companyName: z.string().nullable(),
  role: z.enum(['owner', 'manager']),
  since: IsoDateTime,
});

export const listBrandMembersAdmin = defineRoute({
  method: 'get',
  path: '/api/brands/{id}/members',
  access: 'admin',
  summary: '브랜드의 소속 계정과 역할',
  params: IdParams,
  response: z.object({ members: z.array(BrandMemberAdmin) }),
});

/**
 * 브랜드 이용 상태를 운영자가 정한다 — 이용 중(active) ↔ 결제 대기(confirmed) (261017-brand-status-by-operator).
 * ★ 결제가 안 돼도 **자동으로 결제 대기로 돌리지 않는다** — 알림(`billing:brand:<id>`)을 보고 운영자가 여기서 바꾼다.
 * 승인 대기(pending)는 가입 승인으로만 넘긴다(409). 결제 대기로 돌려도 이용을 연 적이 있으면 수집은 계속한다.
 * 계약 종료는 상태가 아니라 **삭제**다(`deleteBrand`).
 */
export const setBrandStatus = defineRoute({
  method: 'post',
  path: '/api/brands/{id}/status',
  access: 'admin',
  summary: '브랜드 이용 상태 변경 — 이용 중 ↔ 결제 대기',
  params: IdParams,
  body: z.object({ status: z.enum(['active', 'confirmed']) }).strict(),
  response: z.object({ brand: BrandDetail }),
});

/**
 * 테스트 브랜드 표시 (261023-test-brand) — 양방향. 테스트면 운영자 화면의 「테스트」 탭으로 옮겨 가고,
 * 수집이 실제 질문 뒤로 밀리며 용량·결손·결제 알림에서 빠진다
 */
export const setBrandTest = defineRoute({
  method: 'post',
  path: '/api/admin/brands/{id}/test',
  access: 'admin',
  summary: '테스트 브랜드 표시 변경',
  params: IdParams,
  body: z.object({ isTest: z.boolean() }).strict(),
  response: z.object({ brand: BrandDetail }),
});

/**
 * 브랜드 삭제 = **계약 종료** (261022-brand-delete-is-contract-end). 한 트랜잭션에서 브랜드를 지우고(soft),
 * 살아 있는 구독을 즉시 해지하고(사유 `brand_deleted`, 제안은 철회), 질문을 지운다(수집 정지).
 * 결과를 기다리는 결제가 있으면 409 `charge-in-flight`. 소속·계정·카드·기사·숏폼·과거 샘플은 남는다 — 복구할 수 있다.
 * 소속 계정은 이 브랜드를 더 볼 수 없다 — 다른 브랜드가 없으면 「브랜드 없음」으로 로그인해 브랜드를 추가할 수 있다
 */
export const deleteBrand = defineRoute({
  method: 'delete',
  path: '/api/brands/{id}',
  access: 'admin',
  summary: '브랜드 삭제(계약 종료) — 구독 즉시 해지·질문 삭제를 함께. 「삭제된 브랜드」에서 복구한다',
  params: IdParams,
  response: z.object({ canceledSubscriptions: z.number().int(), deletedQueries: z.number().int() }),
});

export const DeletedBrand = z.object({
  id: z.uuid(),
  name: z.string(),
  deletedAt: IsoDateTime,
  /** 브랜드와 **같은 순간**에 지워진 질문 수 — 복구하면 이것들이 함께 돌아온다 */
  queryCount: z.number().int(),
  /** 살아 있는 소속 계정 수 — 복구하면 이 계정들이 다시 들어온다 */
  accountCount: z.number().int(),
  homepageUrl: z.string().nullable(),
});
export type DeletedBrand = z.infer<typeof DeletedBrand>;

export const listDeletedBrands = defineRoute({
  method: 'get',
  path: '/api/admin/brands/deleted',
  access: 'admin',
  summary: '삭제된 브랜드(계약 종료 포함) — 최근에 지운 것이 먼저',
  query: BrandKindQuery,
  response: z.object({ brands: z.array(DeletedBrand) }),
});

/**
 * 삭제 되돌리기 — 브랜드와 **같은 순간에 지워진 질문**을 함께 되살린다(그 뒤에 따로 지운 질문은 그대로).
 * 해지된 구독은 되살리지 않는다 — 새로 제안한다. 상태(active·confirmed)는 지우기 전 값 그대로다
 */
export const restoreBrand = defineRoute({
  method: 'post',
  path: '/api/admin/brands/{id}/restore',
  access: 'admin',
  summary: '삭제된 브랜드 복구 — 함께 지운 질문도 되살린다. 구독은 새로 제안한다',
  params: IdParams,
  response: z.object({ brand: BrandDetail, restoredQueries: z.number().int() }),
});

// ── 타깃 질의 ───────────────────────────────────────────────────────────
export const Query = z.object({
  id: z.uuid(),
  brandId: z.uuid(),
  brandName: z.string(),
  query: z.string(),
  /** 고객 브랜드명의 사본 — 질문 단위로는 받지 않는다 (261008-client-is-brand) */
  brandNames: z.array(z.string()),
  samplesPerDay: z.number().int(),
  /**
   * 온보딩 쓰로틀링 적용 여부 — **admin 에게만 값이 있다**.
   * getQuery·listQueries 는 scoped 라 고객도 부른다. 그냥 실으면 설정이 API 로 새어 나간다
   * (260921-report-snapshot-and-onboarding-throttle).
   */
  onboardingEnabled: z.boolean().nullable(),
  /**
   * 그 질문의 온보딩 기간(일) — **admin 에게만**. 기본값은 `ONBOARDING_DAYS_DEFAULT` 지만
   * 질문마다 다르다 (261001-onboarding-period-per-query). 화면은 상수가 아니라 이 값을 읽는다
   */
  onboardingDays: z.number().int().nullable(),
  deleted: z.boolean(),
  createdAt: IsoDateTime,
});
export type Query = z.infer<typeof Query>;

export const listQueries = defineRoute({
  method: 'get',
  path: '/api/queries',
  access: 'scoped',
  summary: '내 범위의 타깃 질의',
  /** `brandKind` 는 운영자에게만 적용된다 */
  query: BrandKindQuery.extend({
    brandId: z.uuid().optional(),
    includeDeleted: z.enum(['true', 'false']).optional(),
  }),
  response: z.object({ queries: z.array(Query) }),
});
export const getQuery = defineRoute({
  method: 'get',
  path: '/api/queries/{id}',
  access: 'scoped',
  summary: '타깃 질의 한 건',
  params: IdParams,
  response: z.object({ query: Query }),
});

/** 브랜드명은 받지 않는다 — 서버가 고객의 브랜드명으로 채운다 (261008-client-is-brand) */
const QueryInput = {
  query: z.string().trim().min(2).max(300),
  samplesPerDay: z.number().int().min(0).max(48),
  /** 생략하면 생성 시 true(온보딩 적용), 수정 시 기존 값 유지 */
  onboardingEnabled: z.boolean().optional(),
  /** 생략하면 생성 시 `ONBOARDING_DAYS_DEFAULT`, 수정 시 기존 값 유지 */
  onboardingDays: z.number().int().min(ONBOARDING_DAYS_MIN).max(ONBOARDING_DAYS_MAX).optional(),
};

export const createQuery = defineRoute({
  method: 'post',
  path: '/api/queries',
  access: 'admin',
  summary: '타깃 질의 생성',
  body: z.object({ brandId: z.uuid(), ...QueryInput }).strict(),
  response: z.object({ query: Query, warnings: z.array(z.string()) }),
});
export const updateQuery = defineRoute({
  method: 'patch',
  path: '/api/queries/{id}',
  access: 'admin',
  summary: '타깃 질의 수정 — 문구(query)와 소속 고객(brandId)은 바꿀 수 없다',
  params: IdParams,
  body: z
    .object({
      samplesPerDay: QueryInput.samplesPerDay.optional(),
      /** 바꾸면 **다음 스냅샷부터** 효력이 있다. 이미 만들어진 report_daily 행은 그대로다 */
      onboardingEnabled: QueryInput.onboardingEnabled,
      /** 같은 규율 — 기간을 바꿔도 이미 만들어진 행의 계수는 움직이지 않는다 */
      onboardingDays: QueryInput.onboardingDays,
    })
    .strict(),
  response: z.object({ query: Query, warnings: z.array(z.string()) }),
});
export const deleteQuery = defineRoute({
  method: 'delete',
  path: '/api/queries/{id}',
  access: 'admin',
  summary: '타깃 질의 삭제(soft) — 수집 중단, 과거 샘플은 남는다',
  params: IdParams,
  response: Ok,
});

// ── 우리 사이트 (admin) ─────────────────────────────────────────────────
export const Site = z.object({
  id: z.uuid(),
  urlPrefix: z.string(),
  name: z.string(),
  kind: z.enum(SITE_KINDS).nullable(),
  /** 우리 CF 계정 밖(가비아 DNS 등) — 크롤 대조 경고에서 뺀다(261026-site-outside-cf) */
  outsideCf: z.boolean(),
  createdAt: IsoDateTime,
});

export const listSites = defineRoute({
  method: 'get',
  path: '/api/sites',
  access: 'admin',
  summary: '우리가 운영하는 사이트',
  response: z.object({ sites: z.array(Site) }),
});
export const createSite = defineRoute({
  method: 'post',
  path: '/api/sites',
  access: 'admin',
  summary: '우리 사이트 등록 — url_prefix 는 스킴·www 없이 정규화된다',
  body: z
    .object({
      urlPrefix: z.string().trim().min(3).max(300),
      name: z.string().trim().min(1).max(120),
      kind: z.enum(SITE_KINDS).nullable(),
      outsideCf: z.boolean().optional(),
    })
    .strict(),
  response: z.object({ site: Site }),
});
export const updateSite = defineRoute({
  method: 'patch',
  path: '/api/sites/{id}',
  access: 'admin',
  summary: '우리 사이트의 CF 계정 밖 여부 — 크롤 대조(missing-zone) 경고에만 쓰인다',
  params: IdParams,
  body: z.object({ outsideCf: z.boolean() }).strict(),
  response: z.object({ site: Site }),
});
export const deleteSite = defineRoute({
  method: 'delete',
  path: '/api/sites/{id}',
  access: 'admin',
  summary: '우리 사이트 삭제(soft) — 과거 인용이 조회 시 재분류된다',
  params: IdParams,
  response: Ok,
});

/** 삭제된 사이트 — 계약 종료로 철거한 홈페이지 포함(261026-retired-site-delete-from-registry) */
export const DeletedSite = Site.extend({ deletedAt: IsoDateTime });
export type DeletedSite = z.infer<typeof DeletedSite>;

export const listDeletedSites = defineRoute({
  method: 'get',
  path: '/api/sites/deleted',
  access: 'admin',
  summary: '삭제된 우리 사이트 — 최근에 지운 것이 먼저',
  response: z.object({ sites: z.array(DeletedSite) }),
});
export const restoreSite = defineRoute({
  method: 'post',
  path: '/api/sites/{id}/restore',
  access: 'admin',
  summary: '삭제된 우리 사이트 복구 — 같은 주소가 다시 등록돼 있으면 409',
  params: IdParams,
  response: z.object({ site: Site }),
});
